// Shared types and Zod schemas used by both client and server.
// Add migration-by-migration: when porting a feature from legacy-php/,
// define its DTOs and validation schemas here so client and server stay in sync.
import { z } from 'zod';
import { normalizeFolderKey } from './folders.js';
export * from './chant.js';
export * from './chant-doc.js';
export * from './chant-vars.js';
export * from './sankalpa.js';
export * from './namavali.js';
export * from './folders.js';
// NOTE: the pañcāṅga COORDINATE marks (./sankalpa-coord-marks, ~100 kB of
// generated token tables covering every saṃvatsara / nakṣatra / yoga / karaṇa)
// are deliberately NOT re-exported here. Only the server reads them — it
// attaches the right ones to the day's coordinates and ships just those over
// the API — so they are reached through the "@vedaunion/shared/sankalpa-coords"
// subpath and never land in the browser bundle.
// NOTE: astronomy (./astro) is intentionally NOT re-exported here. It pulls in
// astronomy-engine, and is consumed only by the lazy /panchang route via the
// "@vedaunion/shared/astro" subpath — keeping astronomy-engine out of the main
// bundle. Import astro helpers from "@vedaunion/shared/astro", not from here.
export const HealthResponse = z.object({
    status: z.enum(['ok', 'degraded']),
    db: z.enum(['connected', 'unreachable']),
    error: z.string().optional(),
});
export const Role = z.enum(['student', 'youth', 'mentor', 'editor', 'admin', 'developer']);
// -----------------------------------------------------------------------------
// Auth — Phase 1
// -----------------------------------------------------------------------------
/**
 * Email — lowercased, trimmed, valid format. Re-used by every auth schema.
 */
export const Email = z
    .string()
    .trim()
    .toLowerCase()
    .email('Please enter a valid email address.')
    .max(255);
/**
 * Password rules: ≥12 chars. We DO NOT mandate uppercase/symbol/digit rules —
 * those produce worse passwords on average. The server additionally rejects
 * a curated list of trivially-guessable strings (see server/lib/common-passwords).
 */
export const Password = z
    .string()
    .min(12, 'Password must be at least 12 characters.')
    .max(128, 'Password is too long.');
/** Display name shown in the dashboard and to other members. */
export const DisplayName = z
    .string()
    .trim()
    .min(1, 'Please enter a display name.')
    .max(100, 'Display name is too long.');
/**
 * ISO-3166-1 alpha-2 country code (e.g. "HR", "SI", "US"). Optional everywhere.
 */
export const CountryCode = z
    .string()
    .trim()
    .toUpperCase()
    .length(2, 'Country code must be 2 letters.')
    .regex(/^[A-Z]{2}$/);
/** Date of birth as ISO date string (YYYY-MM-DD). Validated separately for sanity. */
export const DateOfBirth = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the format YYYY-MM-DD.')
    .refine((d) => {
    const t = Date.parse(d);
    if (Number.isNaN(t))
        return false;
    const date = new Date(t);
    const now = new Date();
    return date < now && date > new Date('1900-01-01');
}, 'Date of birth seems off.');
export const PreferredLanguage = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(8);
// -- Request schemas --
export const RegisterRequest = z
    .object({
    email: Email,
    password: Password,
    displayName: DisplayName,
    dateOfBirth: DateOfBirth.optional(),
})
    .strict();
export const LoginRequest = z
    .object({
    email: Email,
    password: z.string().min(1, 'Please enter your password.').max(128),
})
    .strict();
export const ForgotPasswordRequest = z
    .object({
    email: Email,
})
    .strict();
export const ResetPasswordRequest = z
    .object({
    token: z.string().min(16).max(256),
    newPassword: Password,
})
    .strict();
export const UpdateProfileRequest = z
    .object({
    displayName: DisplayName.optional(),
    firstName: z.string().trim().max(100).nullable().optional(),
    lastName: z.string().trim().max(100).nullable().optional(),
    dateOfBirth: DateOfBirth.nullable().optional(),
    country: CountryCode.nullable().optional(),
    preferredLanguage: PreferredLanguage.nullable().optional(),
    gotra: z.string().trim().max(64).nullable().optional(),
})
    .strict();
// -----------------------------------------------------------------------------
// User preferences — persisted per account (JSON column on users) for logged-in
// users, mirrored to localStorage for guests. Every field is defaulted so a
// fresh account (or a guest with no stored prefs) resolves to a complete,
// coherent object. `theme` default is LIGHT (as the app has always been); it
// can be changed to dark or system (follows prefers-color-scheme).
// -----------------------------------------------------------------------------
export const ThemePreference = z.enum(['light', 'dark', 'system']);
/** auto = follow the browser locale. Explicit forms pin the field order. */
export const DateFormatPreference = z.enum(['auto', 'dmy', 'mdy', 'ymd', 'iso']);
export const TimeFormatPreference = z.enum(['auto', 'h12', 'h24']);
export const ChantScript = z.enum(['iast', 'devanagari', 'telugu', 'tamil']);
export const ChantSecondaryScript = z.enum(['none', 'iast', 'devanagari', 'telugu', 'tamil']);
/** Rendering preferences for the embeddable chant reader. */
export const ChantPreferences = z.object({
    primaryScript: ChantScript.catch('iast').default('iast'),
    secondaryScript: ChantSecondaryScript.catch('none').default('none'),
    marks: z.boolean().catch(true).default(true), // svara / holding / anusvāra marks
    translation: z.boolean().catch(false).default(false), // show the English gloss
    grammar: z.boolean().catch(true).default(true), // word-tap grammar popovers
    audio: z.boolean().catch(true).default(true), // show per-verse play buttons
    fontScale: z.number().min(0.8).max(1.6).catch(1).default(1),
    mode: z.enum(['read', 'practice']).catch('read').default('read'),
    audioSpeed: z.number().min(0.5).max(2).catch(1).default(1), // recitation playback rate
    // Directions (the reader's non-mantra prose: what to do at each step).
    // `actions` keeps `do` and `caution` and hides `note` / `option` — a person
    // mid-pūjā who turned translation off still needs to know to ring the bell.
    // Turning them fully off must be explicit, never a side effect.
    directions: z.enum(['all', 'actions', 'off']).catch('all').default('all'),
    // Optional / alternative step groups, keyed "<docId>:<groupId>". A choice
    // about one manual must never leak into another.
    variants: z.record(z.union([z.string(), z.boolean()])).catch({}).default({}),
});
/**
 * Saṅkalpa choices — how the reciter's statement of intent is composed.
 *
 * These persist like any other preference (DB for a signed-in user,
 * localStorage for a guest), so a choice made in the Pūjā Vidhi manual is still
 * there on the standalone `/sankalpa` page and on the next visit. `deity` is
 * the DOCUMENT-LEVEL deity: it drives both the saṅkalpa and every `deva` slot
 * in the pūjā mantras — one choice, and the whole rite is re-voiced.
 *
 * `gotra` and `name` are personal data. They live here, in the user's own
 * preferences record, and are never written into a shared or cached document
 * payload.
 */
/*
 * `.catch()` on every field below is LOAD-BEARING.
 *
 * Each field degrades to its own default when it fails to parse, instead of
 * failing the whole object. Without that, one unrecognised value — a deity key
 * renamed between builds, a stale value from an older release, a hand-edited
 * localStorage entry — made the entire `UserPreferences` parse fail, and both
 * readers of it fall back to ALL defaults. So one bad saṅkalpa field silently
 * wiped the reader's theme, date format and chant settings too. No single
 * preference is worth losing three unrelated ones over.
 */
export const SankalpaPreferences = z.object({
    level: z.enum(['simple', 'standard', 'maha']).catch('standard').default('standard'),
    tradition: z.enum(['smarta', 'vaishnava', 'shaiva', 'shakta']).catch('smarta').default('smarta'),
    karma: z
        .enum(['puja', 'japa', 'homa', 'vrata', 'dana', 'snana', 'parayana', 'archana', 'abhisheka', 'dhyana'])
        .catch('puja')
        .default('puja'),
    kamana: z
        .enum(['dharmic', 'health', 'peace', 'knowledge', 'obstacles', 'prosperity',
        'progeny', 'longevity', 'liberation', 'protection', 'allbeings'])
        .catch('dharmic')
        .default('dharmic'),
    deity: z
        .enum(['parameshvara', 'ganesha', 'shiva', 'vishnu', 'devi', 'surya', 'hanuman',
        'lakshmi', 'sarasvati', 'durga', 'krishna', 'rama', 'subrahmanya', 'dattatreya', 'gayatri'])
        .catch('parameshvara')
        .default('parameshvara'),
    /** Has the reader ACTIVELY picked a deity, as opposed to never opening the
     *  setting? AUTHORING-CHANTS §5E rule 3: an untouched preference is not a
     *  choice, and letting the default fill the pūjā rewrites the owner's
     *  liturgy — his deck reads `asmin bimbe śrī devaṁ (or the chosen deity)`.
     *
     *  This used to be INFERRED as `deity !== 'parameshvara'`, which made the
     *  default value unpickable: a reader who deliberately chose Parameśvara got
     *  exactly the behaviour of never having chosen, and the rite silently stayed
     *  at `deva`. The intent is about the ACT of choosing, so it needs its own
     *  field. Old stored rows simply lack it and read as `false`. */
    deityChosen: z.boolean().catch(false).default(false),
    placeFrame: z.enum(['bharata', 'adapted']).catch('adapted').default('adapted'),
    gotra: z.string().max(80).catch('kāśyapa').default('kāśyapa'),
    name: z.string().max(120).catch('').default(''),
});
/** How a list of materials is drawn. Grid = poster tiles, list = dense rows. */
export const MaterialView = z.enum(['grid', 'list']);
export const LibraryPreferences = z.object({
    /** Remembered across documents and sessions — a reader who prefers rows keeps
     *  rows everywhere, rather than re-toggling on every document. */
    view: MaterialView.catch('grid').default('grid'),
});
export const UserPreferences = z.object({
    theme: ThemePreference.catch('light').default('light'),
    dateFormat: DateFormatPreference.catch('auto').default('auto'),
    timeFormat: TimeFormatPreference.catch('auto').default('auto'),
    chant: ChantPreferences.default({}),
    sankalpa: SankalpaPreferences.default({}),
    library: LibraryPreferences.default({}),
});
/** The complete default preferences object (a fresh account / guest). */
export const DEFAULT_PREFERENCES = UserPreferences.parse({});
/**
 * The ONE way to read a STORED preferences record — the server's
 * `resolvePreferences` and the client's guest reader both go through it, so the
 * same tolerance exists on both sides.
 *
 * An unreadable record (bad JSON, not an object) gives defaults. A readable
 * record with one bad FIELD keeps every other field: the per-field `.catch()`
 * above turns the bad one into its own default rather than discarding the rest.
 */
export function parseUserPreferences(raw) {
    let candidate = raw;
    if (typeof candidate === 'string') {
        try {
            candidate = JSON.parse(candidate);
        }
        catch {
            return DEFAULT_PREFERENCES;
        }
    }
    if (candidate === null || candidate === undefined)
        return DEFAULT_PREFERENCES;
    if (typeof candidate !== 'object')
        return DEFAULT_PREFERENCES;
    const parsed = UserPreferences.safeParse(candidate);
    return parsed.success ? parsed.data : DEFAULT_PREFERENCES;
}
/** PATCH body — every field optional; the nested `chant` and `sankalpa` objects
 *  are shallow-merged server-side (and client-side, in preferences.tsx). */
export const UpdateUserPreferences = z
    .object({
    theme: ThemePreference.optional(),
    dateFormat: DateFormatPreference.optional(),
    timeFormat: TimeFormatPreference.optional(),
    chant: ChantPreferences.partial().optional(),
    sankalpa: SankalpaPreferences.partial().optional(),
    library: LibraryPreferences.partial().optional(),
})
    .strict();
// -- Response schemas --
export const PublicUser = z.object({
    id: z.number().int(),
    email: z.string(),
    displayName: z.string(),
    role: Role,
    isActive: z.boolean(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    dateOfBirth: z.string().nullable(), // ISO date string
    country: z.string().nullable(),
    preferredLanguage: z.string().nullable(),
    gotra: z.string().nullable(),
    preferences: UserPreferences,
    emailVerifiedAt: z.string().nullable(), // ISO datetime
    createdAt: z.string(), // ISO datetime
    lastLoginAt: z.string().nullable(),
});
export const MeResponse = z.union([
    z.object({ user: PublicUser }),
    z.object({ user: z.null() }),
]);
/**
 * Successful auth response — carries the user; cookie is set in HTTP headers.
 *
 * `token` is present ONLY for the packaged mobile app, which sends
 * `X-Vedaunion-Client: app` because it cannot receive the HttpOnly cookie from
 * its own origin. Browsers never see this field. See server/src/routes/auth.ts.
 */
export const AuthResponse = z.object({
    user: PublicUser,
    token: z.string().optional(),
});
// =============================================================================
// Mobile app releases
// =============================================================================
/**
 * The currently published Android build, as reported by GET /api/app/release.
 *
 * Written by scripts/publish-apk.sh next to the APK on the media host, so the
 * download page never states a version, size or date that the file it links to
 * doesn't actually have. `url` is a STABLE filename — each release overwrites it
 * — which is what makes a link already shared with someone keep working and
 * always resolve to the newest build.
 */
export const AppRelease = z.object({
    platform: z.literal('android'),
    /** Human version, from versionName in client/android/app/build.gradle. */
    version: z.string(),
    /** Monotonic build number, from versionCode. */
    versionCode: z.number().int().nonnegative(),
    url: z.string().url(),
    sizeBytes: z.number().int().positive(),
    /** sha256 of the APK, so a download can be checked against the signed build. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** ISO-8601 UTC instant the build was published. */
    publishedAt: z.string(),
    minAndroid: z.string().optional(),
});
/**
 * The desktop installer.
 *
 * A separate shape from `AppRelease` because the facts differ: there is no
 * monotonic build number (Tauri versions by string), and there IS a signing
 * status, which the page has to be able to state. Without an Authenticode
 * certificate SmartScreen warns on every download until the binary earns
 * reputation — someone should meet that in the page, not at the warning.
 */
export const DesktopRelease = z.object({
    platform: z.enum(['windows', 'macos', 'linux']),
    version: z.string(),
    url: z.string().url(),
    sizeBytes: z.number().int().positive(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    publishedAt: z.string(),
    /** Code-signed with a certificate the OS trusts. `false` is honest, not a
     *  defect — see scripts/publish-desktop.sh. */
    signed: z.boolean().default(false),
    minOs: z.string().optional(),
});
/**
 * GET /api/app/release. Each field is null when no manifest has been published
 * yet, or when the media host can't be reached — the page then shows its
 * "check back" state rather than a broken button. iOS has no build to report;
 * macOS is prepared but needs an Apple Developer ID before it can exist at all.
 */
export const AppReleaseResponse = z.object({
    android: AppRelease.nullable(),
    ios: z.null(),
    windows: DesktopRelease.nullable().default(null),
});
/** Generic { ok: true } shape returned by logout and password-reset endpoints. */
export const OkResponse = z.object({ ok: z.literal(true) });
// =============================================================================
// Calendar — Phase 2
// =============================================================================
export const EventCategory = z.enum([
    'class',
    'satsang',
    'sadhana',
    'fire-ceremony',
    'festival',
    'holiday',
    'observance',
    'astrological',
    'retreat',
    'serving',
    'gathering',
    'other',
]);
export const EventLocationType = z.enum(['in-person', 'online', 'hybrid']);
export const EventVisibility = z.enum(['public', 'members', 'private']);
export const EventStatus = z.enum(['draft', 'published', 'cancelled']);
const Slug = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug may only contain lowercase letters, digits, and hyphens.');
const NullableTrimmedString = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v ?? null));
const Iso8601 = z.string().refine((v) => !Number.isNaN(Date.parse(v)), {
    message: 'Must be an ISO-8601 datetime.',
});
const EventBaseShape = {
    slug: Slug.optional(),
    title: z.string().trim().min(1).max(200),
    summary: NullableTrimmedString(280),
    description: NullableTrimmedString(20000),
    category: EventCategory.default('other'),
    startsAt: Iso8601,
    endsAt: Iso8601,
    allDay: z.boolean().default(false),
    /** Date fixed, time not announced yet — shown as "Time TBA". */
    timeTbd: z.boolean().default(false),
    timezone: z.string().trim().min(1).max(64).default('Europe/Ljubljana'),
    locationType: EventLocationType.default('in-person'),
    location: NullableTrimmedString(255),
    onlineUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    coverImage: NullableTrimmedString(500),
    capacity: z.number().int().positive().max(100000).nullable().optional(),
    visibility: EventVisibility.default('public'),
    status: EventStatus.default('draft'),
    recurrenceRule: NullableTrimmedString(500),
    recurrenceEndsAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
};
export const EventInput = z
    .object(EventBaseShape)
    .strict()
    .refine((v) => Date.parse(v.endsAt) >= Date.parse(v.startsAt), {
    path: ['endsAt'],
    message: 'End must be at or after start.',
});
/** PATCH variant — every field optional, no end-after-start refinement (the
 *  server re-checks against the merged final state if both are present). */
export const EventUpdate = z.object(EventBaseShape).partial().strict();
/**
 * Lean article reference embedded inside `PublicEvent.relatedArticles`. We
 * inline this above the `PublicEvent` schema so the enums it depends on are in
 * scope. The exported alias `RelatedArticleRef` is wired up further down once
 * the article enums are defined.
 */
const _RelatedArticleRefInner = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    excerpt: z.string().nullable(),
    publishedAt: z.string().nullable(),
    category: z.string(),
    status: z.string(),
});
/**
 * Lean document reference embedded inside `PublicEvent.relatedDocuments`. Same
 * inlining trick as `_RelatedArticleRefInner` — the public alias `DocumentRef`
 * is exported below once the document schemas are in scope.
 */
const _RelatedDocumentRefInner = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    coverImage: z.string().nullable(),
    excerpt: z.string().nullable(),
    status: z.string(),
    publishedAt: z.string().nullable(),
});
export const PublicEvent = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    description: z.string().nullable(),
    category: EventCategory,
    startsAt: z.string(),
    endsAt: z.string(),
    allDay: z.boolean(),
    timeTbd: z.boolean(),
    timezone: z.string(),
    locationType: EventLocationType,
    location: z.string().nullable(),
    onlineUrl: z.string().nullable(),
    coverImage: z.string().nullable(),
    capacity: z.number().int().nullable(),
    visibility: EventVisibility,
    status: EventStatus,
    recurrenceRule: z.string().nullable(),
    recurrenceEndsAt: z.string().nullable(),
    createdBy: z.number().int().nullable(),
    authorName: z.string().nullable(),
    rsvpCount: z.number().int().nonnegative(),
    /** Number of "maybe" RSVPs — surfaced separately from `rsvpCount` (which is "going" only). */
    maybeCount: z.number().int().nonnegative(),
    /** Has the event reached its capacity? `false` when capacity is null. */
    isFull: z.boolean(),
    myRsvp: z.enum(['going', 'maybe', 'declined']).nullable(),
    /** When this row represents a single occurrence of a recurring event, this
     *  is `<eventId>:<YYYY-MM-DD>`. For non-recurring base records it equals
     *  `<eventId>:<dateOfStart>` so callers always have a stable key. */
    occurrenceId: z.string(),
    relatedArticles: z.array(_RelatedArticleRefInner),
    relatedDocuments: z.array(_RelatedDocumentRefInner),
    createdAt: z.string(),
    updatedAt: z.string(),
    publishedAt: z.string().nullable(),
});
/** A single attendee row, returned from GET /api/events/:id/rsvps (organiser only). */
export const RsvpEntry = z.object({
    userId: z.number().int(),
    displayName: z.string(),
    status: z.enum(['going', 'maybe', 'declined']),
    note: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RsvpStatus = z.enum(['going', 'maybe', 'declined']);
export const RsvpRequest = z.object({ status: RsvpStatus, note: NullableTrimmedString(1000) }).strict();
// =============================================================================
// Articles — Phase 2
// =============================================================================
export const ArticleStatus = z.enum(['draft', 'review', 'published', 'archived']);
export const ArticleVisibility = z.enum(['public', 'members']);
export const ArticleTemplate = z.enum([
    'blank',
    'editorial',
    'verse-study',
    'news',
    'news-article',
    'longform',
    'event-recap',
    'note',
    'announcement',
    'report',
    'interview',
    'image-essay',
    'reading-list',
    'class-transcript',
]);
export const ArticleCategory = z.enum([
    'news',
    'reflection',
    'scripture',
    'class-notes',
    'festival-recap',
    'announcement',
    'interview',
    'other',
]);
/**
 * Tiptap document — kept loose because the editor evolves. We trust the
 * editor to produce valid JSON; the server still re-renders the HTML so a
 * malformed JSON doc just produces empty HTML rather than crashing.
 */
export const TiptapDoc = z.object({}).passthrough();
export const ArticleInput = z
    .object({
    slug: Slug.optional(),
    title: z.string().trim().min(1).max(250),
    subtitle: NullableTrimmedString(300),
    excerpt: NullableTrimmedString(1000),
    coverImage: NullableTrimmedString(500),
    contentJson: TiptapDoc,
    contentHtml: z.string().max(200000),
    templateKey: ArticleTemplate.default('editorial'),
    category: ArticleCategory.default('news'),
    tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20).optional(),
    status: ArticleStatus.default('draft'),
    visibility: ArticleVisibility.default('public'),
    featured: z.boolean().default(false),
    relatedEventId: z.number().int().positive().nullable().optional(),
    revisionNote: NullableTrimmedString(255),
    /**
     * Optional override for the publish date. Server only honours this when
     * the user has `articles.publish`. Pass `null` to clear; omit to keep the
     * existing default behaviour (set to NOW on first transition to published).
     */
    publishedAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Must be an ISO-8601 datetime.' })
        .nullable()
        .optional(),
})
    .strict();
/** Lean event reference embedded in article responses. */
export const RelatedEventRef = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    category: EventCategory,
    status: EventStatus,
    locationType: EventLocationType,
});
/** Lean article reference embedded in event responses (the inverse direction). */
export const RelatedArticleRef = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    excerpt: z.string().nullable(),
    publishedAt: z.string().nullable(),
    category: ArticleCategory,
    status: ArticleStatus,
});
export const PublicArticle = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    excerpt: z.string().nullable(),
    coverImage: z.string().nullable(),
    contentJson: z.unknown(),
    contentHtml: z.string(),
    templateKey: ArticleTemplate,
    category: ArticleCategory,
    tags: z.array(z.string()),
    status: ArticleStatus,
    visibility: ArticleVisibility,
    featured: z.boolean(),
    authorId: z.number().int().nullable(),
    authorName: z.string().nullable(),
    editorId: z.number().int().nullable(),
    relatedEventId: z.number().int().nullable(),
    relatedEvent: RelatedEventRef.nullable(),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// =============================================================================
// Documents — Phase 4
// =============================================================================
//
// Documents are the resource sibling to Articles. Same block-based body
// (the editor is shared), but their value is in the **attachments** —
// PDFs in different scripts, audio per anuvāka, YouTube classes, external
// links. The body is an introductory overview; the actual textual content
// of the mantra/sūkta/etc. lives in attachments and (later) standalone
// HTML pages keyed off the document.
export const DocumentStatus = z.enum(['draft', 'review', 'published', 'archived']);
export const DocumentVisibility = z.enum(['public', 'members']);
export const DocumentTemplate = z.enum([
    'blank',
    'mantra-page',
    'sukta-page',
    'shloka-page',
    'sadhana-guide',
    'compilation',
]);
export const DocumentTagNamespace = z.enum(['type', 'deity', 'topic']);
/**
 * Script tag for text-bearing PDFs. Devanāgarī covers Sanskrit/Hindi/Marathi;
 * IAST is the romanised standard. We add common South + East Indian scripts
 * up front so a Sri Rudram document can carry its full set on day one.
 */
export const DocumentScript = z.enum([
    'devanagari',
    'iast',
    'tamil',
    'telugu',
    'kannada',
    'malayalam',
    'bengali',
    'gurmukhi',
    'gujarati',
    'oriya',
    'sinhala',
    'grantha',
    'sharada',
    'tibetan',
    'latin',
    'other',
]);
export const DocumentAttachmentKind = z.enum([
    'pdf',
    'audio',
    'video',
    'youtube_video',
    'youtube_playlist',
    'external_link',
    /**
     * Any other downloadable class material — slides, a worksheet, a Word doc, an
     * image, a zip. `pdf` stays its own kind because a PDF is a *text* of the
     * document (it carries a `script`) and the Library treats it as one; `file`
     * is everything a teacher hands out that isn't a text, a recording or a link.
     */
    'file',
]);
const DocSlug = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug may only contain lowercase letters, digits, and hyphens.');
export const DocumentTagInput = z
    .object({
    namespace: DocumentTagNamespace,
    slug: z
        .string()
        .trim()
        .toLowerCase()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Tag slug may only contain lowercase letters, digits, and hyphens.'),
    label: z.string().trim().min(1).max(100),
    description: NullableTrimmedString(500),
    sortOrder: z.number().int().min(0).max(10000).default(0),
})
    .strict();
export const PublicDocumentTag = z.object({
    id: z.number().int(),
    namespace: DocumentTagNamespace,
    slug: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
});
/**
 * Base attachment shape — kept as a plain ZodObject (no refinement) so
 * `.partial()` works on it for the PATCH route. The full create-time
 * schema (`DocumentAttachmentInput`) wraps this with cross-field rules.
 */
const DocumentAttachmentInputBase = z
    .object({
    kind: DocumentAttachmentKind,
    label: z.string().trim().min(1).max(250),
    description: NullableTrimmedString(1000),
    script: DocumentScript.nullable().optional(),
    fileUrl: NullableTrimmedString(500),
    thumbnailUrl: NullableTrimmedString(500),
    externalUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    youtubeVideoId: z
        .string()
        .trim()
        .max(20)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    youtubePlaylistId: z
        .string()
        .trim()
        .max(34)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    durationSec: z.number().int().min(0).max(60 * 60 * 24).nullable().optional(),
    bytes: z.number().int().min(0).nullable().optional(),
    mime: NullableTrimmedString(80),
    /**
     * Section, or a folder path inside one: `Namakam`, `Namakam/Anuvāka 1`.
     * Stored canonical (`./folders.ts`), so `Namakam / Anuvāka 1 /` typed in the
     * dashboard lands in the same folder as `Namakam/Anuvāka 1`.
     */
    groupKey: NullableTrimmedString(64).transform((v) => normalizeFolderKey(v)),
    sortOrder: z.number().int().min(0).max(10000).default(0),
})
    .strict();
export const DocumentAttachmentInput = DocumentAttachmentInputBase.superRefine((v, ctx) => {
    // Enforce kind-specific required field present at create time.
    const need = (cond, path, msg) => {
        if (!cond)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: msg });
    };
    if (v.kind === 'pdf' || v.kind === 'audio' || v.kind === 'video') {
        need(!!v.fileUrl, 'fileUrl', `${v.kind} requires fileUrl`);
    }
    if (v.kind === 'external_link') {
        need(!!v.externalUrl, 'externalUrl', 'external_link requires externalUrl');
    }
    if (v.kind === 'youtube_video') {
        need(!!v.youtubeVideoId, 'youtubeVideoId', 'youtube_video requires youtubeVideoId');
    }
    if (v.kind === 'youtube_playlist') {
        need(!!v.youtubePlaylistId, 'youtubePlaylistId', 'youtube_playlist requires youtubePlaylistId');
    }
});
/** Partial variant for PATCH — every field optional, no cross-field rules. */
export const DocumentAttachmentUpdate = DocumentAttachmentInputBase.partial();
export const PublicDocumentAttachment = z.object({
    id: z.number().int(),
    documentId: z.number().int(),
    kind: DocumentAttachmentKind,
    label: z.string(),
    description: z.string().nullable(),
    script: DocumentScript.nullable(),
    fileUrl: z.string().nullable(),
    /**
     * Poster image for the file itself — page one of a PDF, a frame of a video.
     * Generated offline (`tools/pdf-thumbs.py`), never at request time. Null means
     * "no poster", and the UI then draws a typed placeholder rather than a
     * stand-in image that pretends to be the document.
     */
    thumbnailUrl: z.string().nullable(),
    externalUrl: z.string().nullable(),
    youtubeVideoId: z.string().nullable(),
    youtubePlaylistId: z.string().nullable(),
    durationSec: z.number().int().nullable(),
    bytes: z.number().int().nullable(),
    mime: z.string().nullable(),
    groupKey: z.string().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    /** Resolved metadata when kind = 'youtube_video' / 'youtube_playlist'. */
    youtubeVideo: z
        .object({
        id: z.string(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        durationSec: z.number().int().nullable(),
    })
        .nullable(),
    youtubePlaylist: z
        .object({
        id: z.string(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        itemCount: z.number().int(),
    })
        .nullable(),
});
export const DocumentInput = z
    .object({
    slug: DocSlug.optional(),
    title: z.string().trim().min(1).max(250),
    subtitle: NullableTrimmedString(300),
    excerpt: NullableTrimmedString(1000),
    coverImage: NullableTrimmedString(500),
    contentJson: TiptapDoc,
    contentHtml: z.string().max(200000),
    templateKey: DocumentTemplate.default('blank'),
    status: DocumentStatus.default('draft'),
    visibility: DocumentVisibility.default('public'),
    /** When non-null, surfaces in the Library Pinned section. Server enforces documents.pin. */
    pinnedAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Must be an ISO-8601 datetime.' })
        .nullable()
        .optional(),
    /** Tag IDs to assign on save (replaces the existing assignment set). */
    tagIds: z.array(z.number().int().positive()).max(40).optional(),
    /** Bidirectional "see also" links. Server writes both directions. */
    relatedDocumentIds: z.array(z.number().int().positive()).max(40).optional(),
    revisionNote: NullableTrimmedString(255),
    publishedAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Must be an ISO-8601 datetime.' })
        .nullable()
        .optional(),
})
    .strict();
/**
 * Lean reference embedded in document responses + cross-link UIs. The shape
 * mirrors `_RelatedDocumentRefInner` (used inside `PublicEvent`) — kept as a
 * separate strict export so callers see the typed `DocumentStatus` enum.
 */
export const DocumentRef = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    coverImage: z.string().nullable(),
    excerpt: z.string().nullable(),
    status: DocumentStatus,
    publishedAt: z.string().nullable(),
});
/** Lean event reference embedded in document responses (mirrors RelatedEventRef). */
export const RelatedEventForDocument = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    startsAt: z.string(),
    endsAt: z.string(),
    category: EventCategory,
    status: EventStatus,
    locationType: EventLocationType,
});
export const PublicDocument = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    excerpt: z.string().nullable(),
    coverImage: z.string().nullable(),
    contentJson: z.unknown(),
    contentHtml: z.string(),
    templateKey: DocumentTemplate,
    status: DocumentStatus,
    visibility: DocumentVisibility,
    pinnedAt: z.string().nullable(),
    authorId: z.number().int().nullable(),
    authorName: z.string().nullable(),
    editorId: z.number().int().nullable(),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    tags: z.array(PublicDocumentTag),
    attachments: z.array(PublicDocumentAttachment),
    relatedDocuments: z.array(DocumentRef),
    relatedEvents: z.array(RelatedEventForDocument),
});
/** Featured composition payload for the public/Library landing tab. */
export const LibraryFeatured = z.object({
    pinned: z.array(DocumentRef),
    recent: z.array(DocumentRef),
    randomDocuments: z.array(DocumentRef),
    tagSpotlight: z.array(z.object({
        tag: PublicDocumentTag,
        documents: z.array(DocumentRef),
    })),
});
// =============================================================================
// RBAC — Phase 2
// =============================================================================
export const RoleWithPermissions = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    permissions: z.array(z.string()),
});
export const MeAbilities = z.object({
    user: PublicUser,
    permissions: z.array(z.string()),
});
// =============================================================================
// Media — Phase 4 (file uploads)
// =============================================================================
export const MediaKind = z.enum(['image', 'audio', 'pdf', 'video']);
export const PublicMedia = z.object({
    id: z.number().int(),
    url: z.string(),
    originalFilename: z.string().nullable(),
    kind: MediaKind,
    mime: z.string(),
    bytes: z.number().int().nonnegative(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    durationSec: z.number().nonnegative().nullable(),
    alt: z.string().nullable(),
    ownerId: z.number().int().nullable(),
    ownerName: z.string().nullable(),
    createdAt: z.string(),
});
export const MediaUploadResponse = z.object({ media: PublicMedia });
// =============================================================================
// YouTube — write-side inputs (Phase 3)
// =============================================================================
//
// Shared between client form validation (React Hook Form + Zod resolver) and
// server-side validation in routes/youtube.ts. Lengths and constraints below
// match YouTube Data API v3 documented limits.
/** YouTube's hard caps. Centralised so client char-counters and server Zod
 *  agree on what's accepted. */
export const VIDEO_TITLE_MAX = 100;
export const VIDEO_DESCRIPTION_MAX = 5000;
export const VIDEO_TAGS_TOTAL_MAX = 500;
export const PLAYLIST_TITLE_MAX = 150;
export const PLAYLIST_DESCRIPTION_MAX = 5000;
export const THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;
export const THUMBNAIL_MIN_WIDTH = 1280;
export const THUMBNAIL_MIN_HEIGHT = 720;
export const VideoPrivacy = z.enum(['private', 'unlisted', 'public']);
export const VideoLicense = z.enum(['youtube', 'creativeCommon']);
/** Per-language localization (title + description per BCP-47 code). */
export const VideoLocalization = z.object({
    language: z.string().min(2).max(20),
    title: z.string().min(1).max(VIDEO_TITLE_MAX),
    description: z.string().max(VIDEO_DESCRIPTION_MAX).optional().default(''),
});
/**
 * Base shape — `.partial()`-friendly because there are no .refine() chains
 * on the object itself. Refines live on `VideoMetadataInput` (the
 * full-upload variant) so update inputs can derive from this without
 * rewriting fields.
 */
const VideoMetadataObject = z.object({
    // Tier 1 — every-upload essentials
    title: z
        .string()
        .min(1, 'Title is required')
        .max(VIDEO_TITLE_MAX, `Max ${VIDEO_TITLE_MAX} characters`)
        .refine((s) => !/[<>]/.test(s), {
        message: 'The < and > characters are not allowed',
    }),
    description: z
        .string()
        .max(VIDEO_DESCRIPTION_MAX, `Max ${VIDEO_DESCRIPTION_MAX} characters`)
        .default(''),
    tags: z
        .array(z.string().min(1).max(100))
        .default([])
        .refine((arr) => arr.join(',').length <= VIDEO_TAGS_TOTAL_MAX, {
        message: `Tags total over ${VIDEO_TAGS_TOTAL_MAX} characters`,
    }),
    /**
     * Numeric YouTube category id (e.g. "27" for Education). Empty string is
     * accepted: server-side `buildVideoResource` skips the field, so YouTube
     * applies its own default rather than us injecting an opinionated one.
     */
    categoryId: z.string().default(''),
    privacyStatus: VideoPrivacy.default('private'),
    /** ISO-8601; only valid when privacyStatus = 'private'. */
    publishAt: z.string().datetime().optional().nullable(),
    selfDeclaredMadeForKids: z.boolean().default(false),
    containsSyntheticMedia: z.boolean().default(false),
    /** Playlist IDs to add the video to (after upload completes). */
    playlistIds: z.array(z.string().min(1).max(64)).default([]),
    // Tier 2 — Advanced disclosure
    embeddable: z.boolean().default(true),
    publicStatsViewable: z.boolean().default(true),
    license: VideoLicense.default('youtube'),
    /** BCP-47 language tag (e.g. "en", "hr", "sl", "sa-Deva"). */
    defaultLanguage: z.string().max(20).optional().nullable(),
    defaultAudioLanguage: z.string().max(20).optional().nullable(),
    /** ISO date "YYYY-MM-DD" — when the recording was actually made. */
    recordingDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional()
        .nullable(),
    /** Only honoured by `videos.insert`; ignored on update. */
    notifySubscribers: z.boolean().default(true),
    // Tier 3 — Localizations (per-language overrides)
    localizations: z.array(VideoLocalization).default([]),
});
/**
 * Tier-1 + Tier-2 video metadata. Used at upload-init time. Tier-2 fields
 * default to YouTube's defaults so the client only sends what changes.
 */
export const VideoMetadataInput = VideoMetadataObject.refine((v) => !v.publishAt || v.privacyStatus === 'private', {
    message: 'Scheduled publish is only valid when privacy = private',
    path: ['publishAt'],
}).refine((v) => !v.publishAt || new Date(v.publishAt).getTime() > Date.now(), { message: 'Scheduled publish must be in the future', path: ['publishAt'] });
/**
 * Initial-upload input — same shape as VideoMetadataInput but with the
 * file's content type + length so the server can pre-flight quota and
 * tell YouTube what's coming. Bytes are NOT in this payload — they go
 * directly from the browser to YouTube's session URL.
 */
export const VideoUploadInitInput = z.object({
    metadata: VideoMetadataInput,
    contentType: z.string().min(1).max(100),
    contentLength: z.number().int().positive().max(5 * 1024 * 1024 * 1024),
});
export const VideoUploadInitResponse = z.object({
    /** YouTube resumable upload session URL — client PUTs the file body here. */
    uploadUrl: z.string().url(),
    /** Server-generated correlation id for the audit log + finalize call. */
    uploadId: z.string(),
    /** Approximate session expiry (24h from now). */
    expiresAt: z.string(),
});
export const VideoUploadFinalizeInput = z.object({
    uploadId: z.string(),
    /** YouTube returned this id from the PUT response. */
    videoId: z.string().min(1).max(20),
});
/** Edit input — all fields optional so callers send only what changes. */
export const VideoUpdateInput = VideoMetadataObject.partial().refine((v) => !v.publishAt || !v.privacyStatus || v.privacyStatus === 'private', {
    message: 'Scheduled publish is only valid when privacy = private',
    path: ['publishAt'],
});
// -----------------------------------------------------------------------------
// Playlists
// -----------------------------------------------------------------------------
export const PlaylistInput = z.object({
    title: z.string().min(1).max(PLAYLIST_TITLE_MAX),
    description: z.string().max(PLAYLIST_DESCRIPTION_MAX).default(''),
    privacyStatus: VideoPrivacy.default('private'),
});
export const PlaylistUpdateInput = PlaylistInput.partial();
export const PlaylistItemAddInput = z.object({
    videoId: z.string().min(1).max(20),
    position: z.number().int().min(0).optional(),
});
export const PlaylistItemPositionInput = z.object({
    position: z.number().int().min(0),
});
// -----------------------------------------------------------------------------
// Captions
// -----------------------------------------------------------------------------
export const CaptionUploadInput = z.object({
    language: z.string().min(2).max(20),
    name: z.string().max(150).default(''),
    isDraft: z.boolean().default(false),
});
export const PublicCaption = z.object({
    id: z.string(),
    videoId: z.string(),
    language: z.string(),
    name: z.string(),
    isDraft: z.boolean(),
    isAutoSynced: z.boolean(),
    trackKind: z.string(),
    audioTrackType: z.string().nullable(),
    status: z.string(),
});
// -----------------------------------------------------------------------------
// Categories (cached read)
// -----------------------------------------------------------------------------
export const VideoCategory = z.object({
    id: z.string(),
    title: z.string(),
    assignable: z.boolean(),
});
// -----------------------------------------------------------------------------
// Quota
// -----------------------------------------------------------------------------
export const QuotaStatus = z.object({
    unitsUsed: z.number().int().nonnegative(),
    unitsLimit: z.number().int().positive(),
    /** ISO timestamp of the next Pacific midnight. */
    resetsAt: z.string(),
    /** Map of action_key → units consumed today. */
    byAction: z.record(z.string(), z.number().int().nonnegative()),
});
// =============================================================================
// Courses — Phase 5 (lessons + quizzes + progress)
// =============================================================================
//
// A course is an ordered sequence of items (lesson | quiz). Lessons reuse the
// block-doc body + the document-attachment pattern for materials (extended with
// kind = 'document' to reference an existing Document). Quizzes are Duolingo-style
// EXERCISE SETS (see ExerciseSetConfig below): the client generates + grades
// randomized, non-repeating exercises from a public word pool and submits the score.
export const CourseStatus = z.enum(['draft', 'review', 'published', 'archived']);
export const CourseVisibility = z.enum(['public', 'members']);
export const CourseLevel = z.enum(['beginner', 'intermediate', 'advanced']);
export const CourseItemKind = z.enum(['lesson', 'quiz']);
export const LessonMaterialKind = z.enum([
    'pdf',
    'audio',
    'video',
    'youtube_video',
    'youtube_playlist',
    'external_link',
    'document',
]);
const CourseSlug = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug may only contain lowercase letters, digits, and hyphens.');
// -- Course --------------------------------------------------------------------
export const CourseInput = z
    .object({
    slug: CourseSlug.optional(),
    title: z.string().trim().min(1).max(250),
    subtitle: NullableTrimmedString(300),
    excerpt: NullableTrimmedString(1000),
    coverImage: NullableTrimmedString(500),
    contentJson: TiptapDoc,
    contentHtml: z.string().max(200000),
    level: CourseLevel.default('beginner'),
    estimatedMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    status: CourseStatus.default('draft'),
    visibility: CourseVisibility.default('public'),
    /** Server only honours this when the user has `courses.publish`. */
    publishedAt: Iso8601.nullable().optional(),
})
    .strict();
export const CourseUpdate = CourseInput.partial();
/** Lean lesson reference embedded in a course's item list. */
export const CourseLessonRef = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    estimatedMinutes: z.number().int().nullable(),
    materialCount: z.number().int(),
});
/** Lean quiz reference embedded in a course's item list. */
export const CourseQuizRef = z.object({
    id: z.number().int(),
    title: z.string(),
    questionCount: z.number().int(),
    passThreshold: z.number().int(),
    maxAttempts: z.number().int().nullable(),
});
/** One ordered item in a course — exactly one of lesson / quiz is set. */
export const PublicCourseItem = z.object({
    id: z.number().int(),
    kind: CourseItemKind,
    sortOrder: z.number().int(),
    lesson: CourseLessonRef.nullable(),
    quiz: CourseQuizRef.nullable(),
});
export const PublicCourse = z.object({
    id: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    excerpt: z.string().nullable(),
    coverImage: z.string().nullable(),
    contentJson: z.unknown(),
    contentHtml: z.string(),
    level: CourseLevel,
    estimatedMinutes: z.number().int().nullable(),
    status: CourseStatus,
    visibility: CourseVisibility,
    authorId: z.number().int().nullable(),
    authorName: z.string().nullable(),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    items: z.array(PublicCourseItem),
});
/** Reorder a course's items: full ordered list of course_item ids. */
export const CourseItemsOrder = z
    .object({ orderedIds: z.array(z.number().int().positive()).max(500) })
    .strict();
// -- Lesson + materials --------------------------------------------------------
export const LessonInput = z
    .object({
    slug: CourseSlug.optional(),
    title: z.string().trim().min(1).max(250),
    subtitle: NullableTrimmedString(300),
    contentJson: TiptapDoc,
    contentHtml: z.string().max(200000),
    estimatedMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    revisionNote: NullableTrimmedString(255),
})
    .strict();
export const LessonUpdate = LessonInput.partial();
const LessonMaterialInputBase = z
    .object({
    kind: LessonMaterialKind,
    label: z.string().trim().min(1).max(250),
    description: NullableTrimmedString(1000),
    script: DocumentScript.nullable().optional(),
    fileUrl: NullableTrimmedString(500),
    externalUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    youtubeVideoId: z
        .string()
        .trim()
        .max(20)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    youtubePlaylistId: z
        .string()
        .trim()
        .max(34)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    documentId: z.number().int().positive().nullable().optional(),
    durationSec: z.number().int().min(0).max(60 * 60 * 24).nullable().optional(),
    bytes: z.number().int().min(0).nullable().optional(),
    mime: NullableTrimmedString(80),
    groupKey: NullableTrimmedString(64),
    sortOrder: z.number().int().min(0).max(10000).default(0),
})
    .strict();
export const LessonMaterialInput = LessonMaterialInputBase.superRefine((v, ctx) => {
    const need = (cond, path, msg) => {
        if (!cond)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: msg });
    };
    if (v.kind === 'pdf' || v.kind === 'audio' || v.kind === 'video') {
        need(!!v.fileUrl, 'fileUrl', `${v.kind} requires fileUrl`);
    }
    if (v.kind === 'external_link')
        need(!!v.externalUrl, 'externalUrl', 'external_link requires externalUrl');
    if (v.kind === 'youtube_video')
        need(!!v.youtubeVideoId, 'youtubeVideoId', 'youtube_video requires youtubeVideoId');
    if (v.kind === 'youtube_playlist')
        need(!!v.youtubePlaylistId, 'youtubePlaylistId', 'youtube_playlist requires youtubePlaylistId');
    if (v.kind === 'document')
        need(!!v.documentId, 'documentId', 'document requires documentId');
});
export const LessonMaterialUpdate = LessonMaterialInputBase.partial();
export const PublicLessonMaterial = z.object({
    id: z.number().int(),
    lessonId: z.number().int(),
    kind: LessonMaterialKind,
    label: z.string(),
    description: z.string().nullable(),
    script: DocumentScript.nullable(),
    fileUrl: z.string().nullable(),
    externalUrl: z.string().nullable(),
    youtubeVideoId: z.string().nullable(),
    youtubePlaylistId: z.string().nullable(),
    documentId: z.number().int().nullable(),
    durationSec: z.number().int().nullable(),
    bytes: z.number().int().nullable(),
    mime: z.string().nullable(),
    groupKey: z.string().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    /** Resolved metadata when kind = 'youtube_video' / 'youtube_playlist'. */
    youtubeVideo: z
        .object({
        id: z.string(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        durationSec: z.number().int().nullable(),
    })
        .nullable(),
    youtubePlaylist: z
        .object({
        id: z.string(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        itemCount: z.number().int(),
    })
        .nullable(),
    /** Resolved reference when kind = 'document'. */
    document: DocumentRef.nullable(),
});
export const PublicLesson = z.object({
    id: z.number().int(),
    courseId: z.number().int(),
    slug: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    contentJson: z.unknown(),
    contentHtml: z.string(),
    estimatedMinutes: z.number().int().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    materials: z.array(PublicLessonMaterial),
});
/** Reorder a lesson's materials: full ordered list of material ids. */
export const LessonMaterialsOrder = z
    .object({ orderedIds: z.array(z.number().int().positive()).max(500) })
    .strict();
// -- Quiz / exercise sets ------------------------------------------------------
//
// A course "quiz" is a Duolingo-style EXERCISE SET: a randomized, non-repeating
// run of exercises generated from a word pool. The config below is stored in
// `quizzes.questions_json`. There are no secret answers to hide — the word pool
// (Devanāgarī ⇄ IAST, grammar form ⇄ label) is public reference data — so the
// engine generates and grades exercises on the client for instant feedback and
// submits the resulting score (the server records it and applies the threshold).
/** Which prompt→answer shape an exercise uses. */
export const ExerciseType = z.enum([
    'pick-iast', // prompt: Devanāgarī token → choose its IAST (multiple choice)
    'pick-devanagari', // prompt: IAST token → choose its Devanāgarī (multiple choice)
    'type-iast', // prompt: Devanāgarī → type IAST (on-screen IAST keyboard)
    'type-devanagari', // prompt: IAST → type Devanāgarī (on-screen Devanāgarī keyboard)
    // Grammar comprehension (uses each pool item's `gloss` as a grammatical label,
    // e.g. "instrumental" / "3rd person sg."). These make the Grammar course test
    // grammar — identifying and producing forms — rather than transliteration.
    'pick-gloss', // prompt: a form (Devanāgarī + IAST) → choose its grammatical label
    'pick-form', // prompt: a grammatical label → choose the matching form (Devanāgarī)
]);
/** Difficulty band selecting which slice of the word pool an exercise draws from. */
export const WordBand = z.enum(['simple', 'cv', 'conjunct', 'mixed']);
/** One step in an exercise set: N exercises of a type drawn from a band. */
export const ExerciseStep = z
    .object({
    type: ExerciseType,
    band: WordBand,
    count: z.number().int().min(1).max(50),
})
    .strict();
/** Config stored on a quiz. `poolKey` selects the client-side word pool. */
export const ExerciseSetConfig = z
    .object({
    version: z.literal(2),
    kind: z.literal('exercise-set'),
    /** Which word pool to draw from, e.g. 'devanagari' or 'grammar-cases'. */
    poolKey: z.string().trim().min(1).max(64),
    steps: z.array(ExerciseStep).min(1).max(40),
})
    .strict();
// ---------------------------------------------------------------------------
// Exercise engine v3 — workbook framework.
//
// A "quiz" is a randomized, non-repeating run of workbook exercises. The client
// generates AND grades the run from public reference data (paradigm tables, word
// pools, sentence templates, illustration scenes) and submits only the score —
// the score contract to the server is unchanged. Every generated item has
// exactly ONE correct answer (or an authored `accept` set) and no also-correct
// distractor — the generator enforces this via a per-item discriminator and a
// seeded-PRNG ambiguity fuzz test (see engine.ts / engine fuzz test).
// ---------------------------------------------------------------------------
export const GrammaticalCase = z.enum(['nom', 'acc', 'ins', 'dat', 'abl', 'gen', 'loc', 'voc']);
export const GrammaticalNumber = z.enum(['sg', 'dual', 'pl']);
export const GrammaticalPerson = z.enum(['p1', 'p2', 'p3']);
/** A grammatical feature slot: nominal (case+number) OR verbal (person+number). */
export const FeatureTuple = z
    .object({
    case: GrammaticalCase.optional(),
    person: GrammaticalPerson.optional(),
    number: GrammaticalNumber,
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.case !== undefined) === (v.person !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A feature tuple must set exactly one of case or person.' });
    }
});
/** Which on-screen keyboard a type-in exercise mounts. */
export const Keyboard = z.enum(['iast', 'devanagari', 'none']);
const exStepBase = {
    /** How many exercises this step contributes (capped by pool availability). */
    count: z.number().int().min(1).max(50),
    /** Category bucket — distractors stay within it; also used for analytics. */
    bucket: z.string().trim().min(1).max(64),
};
/** prompt-field -> answer-field mapping for multiple-choice. */
export const McDirection = z.enum([
    'deva->iast', 'iast->deva', 'form->label', 'label->form', 'word->meaning', 'meaning->word',
]);
export const MultipleChoiceStep = z
    .object({
    type: z.literal('multiple-choice'),
    ...exStepBase,
    poolKey: z.string().trim().min(1).max(64),
    band: WordBand.default('mixed'),
    direction: McDirection,
    optionCount: z.number().int().min(2).max(6).default(4),
})
    .strict();
export const TypeAnswerStep = z
    .object({
    type: z.literal('type-answer'),
    ...exStepBase,
    poolKey: z.string().trim().min(1).max(64),
    band: WordBand.default('mixed'),
    direction: z.enum(['deva->iast', 'iast->deva', 'word->meaning']),
    keyboard: Keyboard,
})
    .strict();
export const FillBlankStep = z
    .object({
    type: z.literal('fill-blank'),
    ...exStepBase,
    /** Sentence-template bank id (templates carry a single inflected slot). */
    templateSet: z.string().trim().min(1).max(64),
    mode: z.enum(['type', 'choose']).default('type'),
    optionCount: z.number().int().min(2).max(6).default(4),
    script: z.enum(['deva', 'iast']).default('iast'),
})
    .strict();
export const InflectStep = z
    .object({
    type: z.literal('inflect'),
    ...exStepBase,
    /** Paradigm-set id (full declension/conjugation tables). */
    paradigmSet: z.string().trim().min(1).max(64),
    /** Restrict drilled slots to these features; empty = all slots in the set. */
    targets: z.array(FeatureTuple).max(48).default([]),
    mode: z.enum(['type', 'choose']),
    optionCount: z.number().int().min(2).max(6).default(4),
    script: z.enum(['deva', 'iast']).default('iast'),
})
    .strict();
export const MatchStep = z
    .object({
    type: z.literal('match'),
    ...exStepBase,
    poolKey: z.string().trim().min(1).max(64),
    band: WordBand.default('mixed'),
    pairKind: z.enum(['form-label', 'word-meaning', 'deva-iast']),
    pairCount: z.number().int().min(3).max(6).default(4),
})
    .strict();
export const IdentifyStep = z
    .object({
    type: z.literal('identify'),
    ...exStepBase,
    /** Template bank with a pre-filled, context-disambiguated marked word. */
    templateSet: z.string().trim().min(1).max(64),
    axis: z.enum(['case', 'number', 'person', 'case+number']),
    optionCount: z.number().int().min(2).max(6).default(4),
})
    .strict();
export const IllustrationStep = z
    .object({
    type: z.literal('illustration'),
    ...exStepBase,
    /** Scene bank id (SVG + the relation it depicts → the entailed feature). */
    sceneSet: z.string().trim().min(1).max(64),
    mode: z.enum(['type', 'choose']).default('choose'),
    optionCount: z.number().int().min(2).max(6).default(4),
    script: z.enum(['deva', 'iast']).default('iast'),
})
    .strict();
/** Assemble shuffled tiles into the correct order. `akshara` mode splits a word
 *  from a pool into its syllable tiles; `sequence` mode uses an authored ordered
 *  bank (e.g. the vowel garland, a paradigm's case order). Exactly one resulting
 *  string is correct, so there is never an ambiguous arrangement. */
export const OrderStep = z
    .object({
    type: z.literal('order'),
    ...exStepBase,
    mode: z.enum(['akshara', 'sequence']),
    /** akshara mode: word pool to draw target words from. */
    poolKey: z.string().trim().min(1).max(64).optional(),
    band: WordBand.default('mixed'),
    /** sequence mode: authored ordered-sequence bank id. */
    sequenceSet: z.string().trim().min(1).max(64).optional(),
    script: z.enum(['deva', 'iast']).default('deva'),
})
    .strict();
/** Sort items into labelled buckets. Each item belongs to exactly one bucket
 *  (a hidden discriminator), so grading is unambiguous. */
export const CategorizeStep = z
    .object({
    type: z.literal('categorize'),
    ...exStepBase,
    /** Categorize bank id (buckets + items, each item tagged with one bucket). */
    categorizeSet: z.string().trim().min(1).max(64),
    /** Items shown per exercise (drawn across the buckets). */
    itemCount: z.number().int().min(3).max(10).default(6),
    script: z.enum(['deva', 'iast', 'label']).default('deva'),
})
    .strict();
/** Fill several blanks in one sentence from a shared word bank. Every blank is
 *  cued (lemma + feature) so its filler is unique; bank distractors never equal
 *  a correct filler and correct fillers are pairwise distinct. */
export const ClozeStep = z
    .object({
    type: z.literal('cloze'),
    ...exStepBase,
    /** Multi-blank sentence-template bank id. */
    clozeSet: z.string().trim().min(1).max(64),
    /** Extra wrong forms added to the shared word bank. */
    distractorCount: z.number().int().min(0).max(6).default(2),
    script: z.enum(['deva', 'iast']).default('deva'),
})
    .strict();
/** Fully parse a marked word in a sentence: select its lemma, its case/person,
 *  and its number. The context fixes a single reading, so each axis has exactly
 *  one correct option. */
export const ParseStep = z
    .object({
    type: z.literal('parse'),
    ...exStepBase,
    /** Identify-style template bank (marked, context-disambiguated word). */
    templateSet: z.string().trim().min(1).max(64),
    /** Which axes to ask. 'feature' = case (nouns) or person (verbs). */
    axes: z.array(z.enum(['lemma', 'feature', 'number'])).min(1).max(3).default(['lemma', 'feature', 'number']),
    optionCount: z.number().int().min(2).max(6).default(4),
})
    .strict();
/** Translate a short sentence (Sanskrit→English or English→Sanskrit) by single
 *  choice. Distractors are translations of OTHER sentences, so exactly one is
 *  right (discriminator = sentence id). */
export const TranslateStep = z
    .object({
    type: z.literal('translate'),
    ...exStepBase,
    /** Translation bank id (sentence ↔ gloss pairs). */
    translateSet: z.string().trim().min(1).max(64),
    direction: z.enum(['sa->en', 'en->sa']).default('sa->en'),
    optionCount: z.number().int().min(2).max(6).default(4),
    script: z.enum(['deva', 'iast']).default('deva'),
})
    .strict();
/** Combine two words by sandhi (or recognise the combined form). The join is
 *  rule-deterministic — one correct result — and distractors are other banks'
 *  joins (discriminator = the joined surface). */
export const SandhiStep = z
    .object({
    type: z.literal('sandhi'),
    ...exStepBase,
    /** Sandhi bank id (a + b → joined, with the rule). */
    sandhiSet: z.string().trim().min(1).max(64),
    mode: z.enum(['choose', 'type']).default('choose'),
    optionCount: z.number().int().min(2).max(6).default(4),
    script: z.enum(['deva', 'iast']).default('deva'),
})
    .strict();
/** A step is exactly one of the workbook exercise types (thirteen). */
export const ExerciseStepV3 = z.discriminatedUnion('type', [
    MultipleChoiceStep, TypeAnswerStep, FillBlankStep, InflectStep, MatchStep, IdentifyStep, IllustrationStep,
    OrderStep, CategorizeStep, ClozeStep, ParseStep, TranslateStep, SandhiStep,
]);
/** v3 exercise-set config stored on a quiz (quizzes.questions_json). */
export const ExerciseSetConfigV3 = z
    .object({
    version: z.literal(3),
    kind: z.literal('exercise-set'),
    poolKey: z.string().trim().min(1).max(64).optional(),
    steps: z.array(ExerciseStepV3).min(1).max(40),
})
    .strict();
/** Accepts either legacy v2 or v3 configs (re-seeding moves all quizzes to v3). */
export const AnyExerciseSetConfig = z.union([ExerciseSetConfig, ExerciseSetConfigV3]);
export const QuizInput = z
    .object({
    title: z.string().trim().min(1).max(250),
    description: NullableTrimmedString(4000),
    passThreshold: z.number().int().min(0).max(100).default(70),
    maxAttempts: z.number().int().min(1).max(1000).nullable().optional(),
    content: AnyExerciseSetConfig,
})
    .strict();
export const QuizUpdate = QuizInput.partial();
/** Learner-facing quiz — the exercise-set config (no secret answers to strip). */
export const PublicQuizForLearner = z.object({
    id: z.number().int(),
    courseId: z.number().int(),
    title: z.string(),
    description: z.string().nullable(),
    passThreshold: z.number().int(),
    maxAttempts: z.number().int().nullable(),
    content: AnyExerciseSetConfig,
    /** Attempts this user has already submitted. */
    attemptsUsed: z.number().int(),
    /** Best score so far, if any. */
    bestScore: z.number().int().nullable(),
});
/** The client runs the set locally and submits the resulting score. */
export const QuizSubmit = z
    .object({
    score: z.number().int().min(0).max(100),
    correct: z.number().int().min(0),
    total: z.number().int().min(1),
})
    .strict();
export const QuizResult = z.object({
    attemptId: z.number().int(),
    quizId: z.number().int(),
    score: z.number().int(),
    passed: z.boolean(),
    passThreshold: z.number().int(),
    submittedAt: z.string(),
});
// -- Progress ------------------------------------------------------------------
export const LessonProgressInput = z
    .object({
    status: z.enum(['in_progress', 'completed']).default('in_progress'),
    lastPosition: NullableTrimmedString(64),
})
    .strict();
export const PublicLessonProgress = z.object({
    lessonId: z.number().int(),
    status: z.enum(['in_progress', 'completed']),
    lastPosition: z.string().nullable(),
    completedAt: z.string().nullable(),
    updatedAt: z.string(),
});
/** A course's progress for the current user — enrollment + derived completion. */
export const PublicCourseProgress = z.object({
    courseId: z.number().int(),
    enrolled: z.boolean(),
    completedItemCount: z.number().int(),
    totalItemCount: z.number().int(),
    percent: z.number().int(),
    lastItemId: z.number().int().nullable(),
    completedAt: z.string().nullable(),
    lessons: z.array(PublicLessonProgress),
});
// -----------------------------------------------------------------------------
// Panchang — Vedic calendar (Prokerala API). Public DTOs consumed by the client.
//
// The server caches the raw Prokerala payloads per day and shapes them into
// these clean, localized DTOs. A term carries three renderings: `en` (the
// romanized name from the API), `iast` (house-style diacritics), and `deva`
// (Devanāgarī, '' when unmapped). Conceptually the day splits into UNIVERSAL
// qualities (tithi, nakṣatra, yoga, karaṇa, paksha, samvat — the same
// everywhere) and LOCAL timings (sun/moon, auspicious/inauspicious windows,
// choghadiya — computed for the configured location, shown labelled as such).
// -----------------------------------------------------------------------------
export const PanchangTermName = z.object({
    en: z.string(),
    iast: z.string(),
    deva: z.string().default(''),
});
/** A tithi / nakṣatra / yoga / karaṇa span. start/end are ISO-8601 strings with
 *  the source offset; the client renders them in the panchang timezone. */
export const PanchangSequenceEntry = z.object({
    id: z.number().int().nullable().optional(),
    name: PanchangTermName,
    start: z.string().nullable().optional(),
    end: z.string().nullable().optional(),
    /** Tithi only — Shukla / Krishna paksha. */
    paksha: PanchangTermName.nullable().optional(),
    /** Nakṣatra only — its ruling planet. */
    lord: PanchangTermName.nullable().optional(),
});
export const PanchangInterval = z.object({
    start: z.string(),
    end: z.string(),
});
/** An auspicious or inauspicious window (Abhijit, Amṛta Kāla, Rāhu, Yamaganda,
 *  Gulika, Dur Muhūrta, Varjyam…). May have multiple disjoint segments. */
export const PanchangPeriod = z.object({
    id: z.number().int().nullable().optional(),
    key: z.string(),
    name: PanchangTermName,
    type: z.enum(['auspicious', 'inauspicious']),
    segments: z.array(PanchangInterval),
});
export const ChoghadiyaSlot = z.object({
    name: PanchangTermName,
    /** Prokerala quality: 'Most Auspicious' | 'Good' | 'Inauspicious' (English). */
    type: z.string(),
    /** Special vela label (Vaar Vela / Kaal Vela / Kaal Ratri) or null. */
    vela: z.string().nullable(),
    isDay: z.boolean(),
    start: z.string(),
    end: z.string(),
});
/** A notable-day badge derived from the panchang (Ekādaśī, Pūrṇimā, Amāvāsyā,
 *  Pradoṣa, Saṅkrānti, special yogas). `kind` drives the marker glyph/colour. */
export const PanchangMarker = z.object({
    key: z.string(),
    label: z.string(),
    deva: z.string().default(''),
    kind: z.enum(['tithi', 'moon', 'sankranti', 'yoga', 'fast', 'festival']),
});
export const PanchangSamvat = z.object({
    /** e.g. { era: 'Vikram Samvat', year: 2081, yearName: 'Pingala', month: 'Jyeshta' } */
    era: z.string(),
    year: z.number().int().nullable().optional(),
    yearName: z.string().nullable().optional(),
    month: z.string().nullable().optional(),
});
/** A festival / observance falling on a day, with a neutral description. */
export const PanchangFestival = z.object({
    id: z.string(),
    /** Display name in IAST. */
    name: z.string(),
    /** Devanāgarī, '' when unmapped. */
    deva: z.string().default(''),
    kind: z.enum(['festival', 'fast', 'moon', 'sankranti', 'ekadashi', 'jayanti', 'observance']),
    /** Neutral, non-prescriptive 1–2 sentence description. */
    description: z.string().default(''),
    /** Tradition/profile this entry belongs to (e.g. 'general', 'vaishnava'). */
    tradition: z.string().optional(),
});
/** A deity name in the three scripts. */
export const PanchangDeity = z.object({
    en: z.string(),
    iast: z.string(),
    deva: z.string(),
});
/** One devatā dedication (weekday / tithi / nakṣatra) with its customary note. */
export const PanchangDevataEntry = z.object({
    deity: PanchangDeity,
    /** 1–2 sentence note: who it is dedicated to + the customary observance. */
    note: z.string(),
    /** Document deity-tag slugs for related-Library lookup. */
    keys: z.array(z.string()).default([]),
});
/** The day's three classical devatā dedications. */
export const PanchangDevata = z.object({
    /** Weekday (vāra) deity — always present. */
    vara: PanchangDevataEntry,
    /** Lunar-day (tithi) deity. */
    tithi: PanchangDevataEntry.nullable(),
    /** Nakṣatra presiding (Vedic) deity. */
    nakshatra: PanchangDevataEntry.nullable(),
    /** Union of all deity-tag slugs for the day. */
    keys: z.array(z.string()).default([]),
});
/** A Library document relevant to one of the day's deities. */
export const PanchangRelatedDocument = z.object({
    slug: z.string(),
    title: z.string(),
    type: z.string().nullable(),
    summary: z.string().nullable(),
    /** The deity-tag slug that matched (for grouping/labelling). */
    deity: z.string().nullable(),
});
/** The full per-day panchang the client renders. `available: false` means the
 *  day isn't in our cache yet (budget/rate limits) — the UI shows a graceful
 *  "not available" state and the events calendar is unaffected. */
export const PanchangDay = z.object({
    date: z.string(),
    available: z.boolean(),
    locationName: z.string(),
    status: z.enum(['ok', 'partial']).optional(),
    fetchedAt: z.string().nullable().optional(),
    vaara: PanchangTermName.nullable().optional(),
    /** The entry running at sunrise — the day's headline tithi/nakṣatra/etc. */
    primary: z
        .object({
        tithi: PanchangSequenceEntry.nullable(),
        nakshatra: PanchangSequenceEntry.nullable(),
        yoga: PanchangSequenceEntry.nullable(),
        karana: PanchangSequenceEntry.nullable(),
    })
        .nullable()
        .optional(),
    tithi: z.array(PanchangSequenceEntry).default([]),
    nakshatra: z.array(PanchangSequenceEntry).default([]),
    yoga: z.array(PanchangSequenceEntry).default([]),
    karana: z.array(PanchangSequenceEntry).default([]),
    sunrise: z.string().nullable().optional(),
    sunset: z.string().nullable().optional(),
    moonrise: z.string().nullable().optional(),
    moonset: z.string().nullable().optional(),
    auspicious: z.array(PanchangPeriod).default([]),
    inauspicious: z.array(PanchangPeriod).default([]),
    choghadiya: z
        .object({ day: z.array(ChoghadiyaSlot), night: z.array(ChoghadiyaSlot) })
        .nullable()
        .optional(),
    samvat: z.array(PanchangSamvat).default([]),
    markers: z.array(PanchangMarker).default([]),
    /** Festivals/observances on this day (with neutral descriptions). */
    festivals: z.array(PanchangFestival).default([]),
    /** The day's deity dedications (weekday / tithi / nakṣatra). Read-time. */
    devata: PanchangDevata.nullable().optional(),
    /** Library documents relevant to the day's deities (full-day reads only). */
    relatedDocuments: z.array(PanchangRelatedDocument).default([]),
});
/** Compact marker set for the month grid (one entry per cached day). */
export const PanchangDayMarkers = z.object({
    date: z.string(),
    tithi: PanchangTermName.nullable().optional(),
    nakshatra: PanchangTermName.nullable().optional(),
    paksha: PanchangTermName.nullable().optional(),
    markers: z.array(PanchangMarker).default([]),
});
export const PanchangRangeResponse = z.object({
    enabled: z.boolean(),
    locationName: z.string(),
    days: z.array(PanchangDayMarkers),
});
/** Public, secret-free status — drives whether the client shows the overlay. */
export const PanchangStatus = z.object({
    enabled: z.boolean(),
    locationName: z.string(),
    coords: z.string(),
    timezone: z.string(),
});
/** Admin-only diagnostics (budget + rate window + cache health). No secrets. */
export const PanchangAdminStatus = z.object({
    enabled: z.boolean(),
    locationName: z.string(),
    coords: z.string(),
    timezone: z.string(),
    budget: z.object({
        month: z.string(),
        used: z.number().int(),
        limit: z.number().int(),
        byEndpoint: z.record(z.string(), z.number()),
    }),
    rateWindow: z.object({
        recentCount: z.number().int(),
        limitPer10Min: z.number().int(),
        nextAllowedAt: z.string().nullable(),
    }),
    tokenExpiresAt: z.string().nullable(),
    cache: z.object({
        days: z.number().int(),
        ok: z.number().int(),
        partial: z.number().int(),
        oldest: z.string().nullable(),
        newest: z.string().nullable(),
    }),
    lock: z.object({
        lockedAt: z.string().nullable(),
        lockedBy: z.string().nullable(),
    }),
    lastFillAt: z.string().nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: z.string().nullable(),
});
